import { Link } from '@tanstack/react-router';
import { Info } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { TranslationKeys } from '@/lib/translations/types';
import { cn } from '@/lib/utils';
import type { FileRouteTypes } from '@/routeTree.gen';

const getColor = (connected: boolean, available: boolean, pending: boolean) => {
  if (pending) {
    return 'bg-gray-200 border-gray-200 rounded-sm animate-pulse dark:bg-background dark:border-background';
  }

  if (!available) {
    return 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500';
  }

  return connected
    ? 'border-green-500 text-black dark:text-white'
    : 'border-red-500 text-black dark:text-white';
};

const getText = (
  connected: boolean,
  available: boolean,
  pending: boolean,
  donePercentage: number | undefined,
) => {
  if (pending) {
    return '...';
  }

  if (!available) {
    return 'Coming soon';
  }

  if (connected && donePercentage !== undefined) {
    return `Connected (${donePercentage}%)`;
  }

  return connected ? `Connected` : 'Not Connected';
};

interface Props {
  logoUrl: string;
  connected: boolean;
  title: TranslationKeys;
  available: boolean;
  onClick?: () => void;
  to?: FileRouteTypes['to'];
  pending?: boolean;
  warnings?: TranslationKeys[];
  donePercentage?: number;
}

export const Integration = ({
  title,
  logoUrl,
  connected,
  available,
  to,
  onClick = () => undefined,
  pending = false,
  warnings = [],
  donePercentage,
}: Props): JSX.Element => {
  const { formatMessage } = useIntl();

  return (
    <div className="border border-gray-400 p-2 flex flex-col items-center rounded-sm gap-2">
      <div className="dark:bg-gray-200 rounded-sm p-2">
        <img className="w-[40px] h-[40px]" src={logoUrl} alt={title} />
      </div>
      <div
        className={cn(
          'text-center border w-full lg:text-sm p-2 text-xs text-ellipsis truncate',
          getColor(connected, available, pending),
        )}
      >
        <p className="text-md">
          {getText(connected, available, pending, donePercentage)}
          {warnings.length === 0 || !connected ? (
            ''
          ) : (
            <TooltipProvider disableHoverableContent>
              <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 inline mb-[2px] ml-1 text-gray-500 cursor-pointer stroke-[3px]" />
                </TooltipTrigger>
                <TooltipContent side="right">
                  <div className="flex flex-col gap-2">
                    {warnings.map(warning => (
                      <p key={warning} className="text-xs">
                        <FormattedMessage id={warning} />
                      </p>
                    ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </p>
      </div>
      {to !== undefined ? (
        <Link to={to} className="w-full">
          <Button
            variant="main"
            disabled={!available}
            className="w-full rounded-sm bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
            onClick={onClick}
          >
            <FormattedMessage
              id="integrations.tryIt.import"
              values={{ value: formatMessage({ id: title }) }}
            />
          </Button>
        </Link>
      ) : (
        <Button
          variant="main"
          disabled={!available}
          className="w-full rounded-sm bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          onClick={onClick}
        >
          <FormattedMessage
            id="integrations.tryIt.import"
            values={{ value: formatMessage({ id: title }) }}
          />
        </Button>
      )}
    </div>
  );
};
